class PredictiveSearch extends HTMLElement {
  constructor() {
    super()

    this.input = this.querySelector('#predictive-search-input')
    this.clearButton = this.querySelector('[data-search-clear]')
    this.predictiveSearchResults = this.querySelector('#predictive-search-target')
    this.loadingEl = this.querySelector('#predictive-search-loading')
    this.searchUrl = this.dataset.searchUrl || '/search'
    this.activeRequest = null

    this.debouncedFetch = this.debounce(() => {
      this.onChange()
    }, 300)

    this.form = this.querySelector('form[role="search"]')
    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault()
        const term = this.withLastTokenWildcard(this.input?.value)
        if (!term) return

        const params = new URLSearchParams(new FormData(this.form))
        params.set('q', term)
        window.location.assign(`${this.form.action}?${params.toString()}`)
      })
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.reset()
        this.input?.focus()
      })
    }

    if (this.input) {
      this.input.addEventListener('input', () => {
        const searchTerm = this.input.value.trim()

        if (!searchTerm.length) {
          this.abortActiveRequest()
          this.clearResults()
          return
        }

        this.setLoading(true)
        this.debouncedFetch()
      })
    }
  }

  withLastTokenWildcard(value) {
    const term = String(value || '').trim()
    if (!term || term.endsWith('*')) return term
    return `${term}*`
  }

  reset() {
    this.debouncedFetch.cancel()
    this.abortActiveRequest()

    if (this.input) {
      this.input.value = ''
    }

    this.clearResults()
  }

  onChange() {
    const searchTerm = this.input.value.trim()

    if (!searchTerm.length) {
      return
    }

    this.getSearchResults(searchTerm)
  }

  getSearchResults(searchTerm) {
    this.abortActiveRequest()

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search-results',
      'resources[type]': 'product,collection,article',
      'resources[limit]': '5',
      'resources[limit_scope]': 'each',
    })

    const controller = new AbortController()
    this.activeRequest = controller

    fetch(`/search/suggest?${params.toString()}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          throw error
        }

        return response.text()
      })
      .then((text) => {
        if (controller.signal.aborted) return

        const parsed = new DOMParser().parseFromString(text, 'text/html')
        const section = parsed.querySelector('#shopify-section-predictive-search-results')

        if (!section || !this.predictiveSearchResults) {
          this.clearResults()
          return
        }

        this.predictiveSearchResults.innerHTML = section.innerHTML
        this.input.setAttribute('aria-expanded', 'true')
      })
      .catch((error) => {
        if (error.name === 'AbortError') return

        this.clearResults()
        throw error
      })
      .finally(() => {
        if (this.activeRequest === controller) {
          this.activeRequest = null
          this.setLoading(false)
        }
      })
  }

  setLoading(isLoading) {
    if (!this.loadingEl) return

    const hasResults = Boolean(this.predictiveSearchResults?.innerHTML.trim())

    this.loadingEl.classList.toggle('hidden', !isLoading)
    this.loadingEl.classList.toggle('flex', isLoading)
    this.loadingEl.setAttribute('aria-busy', isLoading ? 'true' : 'false')

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.toggle('opacity-40', isLoading && hasResults)
      this.predictiveSearchResults.classList.toggle('pointer-events-none', isLoading && hasResults)
    }
  }

  abortActiveRequest() {
    if (this.activeRequest) {
      this.activeRequest.abort()
      this.activeRequest = null
    }
  }

  clearResults() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = ''
    }

    if (this.input) {
      this.input.setAttribute('aria-expanded', 'false')
    }

    this.setLoading(false)
  }

  debounce(fn, wait) {
    let timeoutId

    const debounced = (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn.apply(this, args), wait)
    }

    debounced.cancel = () => {
      clearTimeout(timeoutId)
    }

    return debounced
  }
}

customElements.define('predictive-search', PredictiveSearch)
